/**
 * kelly-0915-editwheel-description-editor-copies-asterisks-ux.
 *
 * Live-measured on web-dev.iyuga.net (bundle main.6f22a36b.js), uid 12675: a
 * real OS-clipboard + Ctrl+V paste of "This is **important** text." into the
 * live Wheel Settings > Description field landed as literal asterisks --
 * innerHTML `<p id="isPasted">This is **important** text.</p>` -- with no
 * bold applied. Root cause: this field has no `characters` prop, so
 * handlePaste()'s early-return (`if (text && characters)`) never fires and
 * the paste falls through to Froala's own native pipeline, which is
 * configured `pastePlain: true` (index.jsx) -- it flattens ALL formatting to
 * plain paragraphs but has zero markdown awareness (the `markdown`
 * plugin bundled in froala-editor/js/plugins/markdown.min.js is not in
 * `pluginsEnabled`), so `**text**` typed or copied from any markdown-aware
 * source (ChatGPT's copy button, Notion, Slack, a raw README) round-trips as
 * inert literal asterisks instead of becoming real bold.
 *
 * This converts common inline markdown emphasis (`**bold**` / `*italic*`)
 * left over after Froala's own paste cleanup into real `<strong>`/`<em>`
 * elements, operating on DOM text nodes only (never HTML/attribute source)
 * so it cannot introduce a markup-injection vector -- `el.textContent =` is
 * used for the captured content, which the DOM always treats as literal text.
 *
 * Scoped to asterisks only (not `_underscore_`/`__underscore__`), matching
 * the reported repro and avoiding false positives on ordinary
 * underscore-bearing text (snake_case identifiers, emails, filenames).
 */

// No whitespace immediately inside the delimiters (CommonMark's own rule for
// emphasis) -- keeps "3 * 4 * 5" and "a ** b" from being misread as markup.
const BOLD_RE = /\*\*(?!\s)([\s\S]+?)(?<!\s)\*\*/;
const ITALIC_RE = /\*(?!\s)([\s\S]+?)(?<!\s)\*/;

function buildFragmentFromMarkdown(doc, text) {
  const frag = doc.createDocumentFragment();
  let remaining = text;
  let matchedAny = false;

  while (remaining.length) {
    const boldMatch = BOLD_RE.exec(remaining);
    const italicMatch = ITALIC_RE.exec(remaining);

    let match = null;
    let tag = null;
    if (boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index)) {
      match = boldMatch;
      tag = "strong";
    } else if (italicMatch) {
      match = italicMatch;
      tag = "em";
    }

    if (!match) break;
    matchedAny = true;

    const before = remaining.slice(0, match.index);
    if (before) frag.appendChild(doc.createTextNode(before));

    const el = doc.createElement(tag);
    el.textContent = match[1];
    frag.appendChild(el);

    remaining = remaining.slice(match.index + match[0].length);
  }

  if (!matchedAny) return null;
  if (remaining) frag.appendChild(doc.createTextNode(remaining));
  return frag;
}

function walkAndConvert(doc, node) {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === 3 /* TEXT_NODE */) {
      if (!/\*/.test(child.nodeValue)) return;
      const frag = buildFragmentFromMarkdown(doc, child.nodeValue);
      if (frag) child.replaceWith(frag);
    } else if (child.nodeType === 1 /* ELEMENT_NODE */) {
      walkAndConvert(doc, child);
    }
  });
}

/**
 * Convert `**bold**` / `*italic*` markdown-style emphasis remaining inside
 * `html` into real `<strong>`/`<em>` elements. Text content only -- tag
 * names and attributes are never touched, and `document.createElement` +
 * `.textContent` means captured markdown content can never smuggle HTML.
 *
 * @param {string} html
 * @param {Document} [doc] - injected for testability; defaults to `document`.
 * @returns {string}
 */
export function convertMarkdownEmphasisToHtml(html, doc = typeof document !== "undefined" ? document : undefined) {
  if (typeof html !== "string" || !html) return html;
  if (!doc) return html;
  if (!html.includes("*")) return html;

  const container = doc.createElement("div");
  container.innerHTML = html;
  walkAndConvert(doc, container);
  return container.innerHTML;
}

export default convertMarkdownEmphasisToHtml;
